import axios from 'axios';
import { MachineState } from '../reducers/machineReducer';
import { MaterialState } from '../reducers/materialReducer';
import { ToolState } from '../reducers/toolReducer';

// Use PHP port if using React dev server
const ENDPOINT = process.env.NODE_ENV === 'development' ?
    'http://localhost:8000/php/Milling_Advisor.php' :
    './php/Milling_Advisor.php';

function isEqual(a: object, b: object) {
    return JSON.stringify(a) === JSON.stringify(b);
}

type UnitType = 'length'|'pressure'|'feed'|'surfacespeed'|'feedpertooth'|'feedperrevolution'|'removalrate';

class API {
    cached = new Map();
    cachedEndmillState: [MaterialState, MachineState]|null = null;
    cachedDrillState: [MaterialState, MachineState]|null = null;


    units: "INCH"|"METRIC" = sessionStorage.getItem('advisor-units') as "INCH"|"METRIC" || "INCH";
    standards: string = sessionStorage.getItem('advisor-standards') || "";

    async calculate(material: MaterialState, machine: MachineState, tool: ToolState) {
        const resp = await axios.post(ENDPOINT + "/calculate", {
            tool,
            machine,
            material,
            units: this.units
        });

        return resp.data;
    }

    clearCache() {
        this.cached.clear();
        this.cachedEndmillState = null;
        this.cachedDrillState = null;
    }
    
    displayPreferredUnits(type: UnitType) {
        switch(type) {
            case 'length':
                return this.units === 'INCH' ? 'in' : 'mm';
            case 'pressure':
                return this.units === 'INCH' ? 'KSI' : 'MPa';
            case 'feed':
                return this.units === 'INCH' ? 'IPM' : 'MMPM';
            case 'surfacespeed':
                return this.units === 'INCH' ? 'SFM' : 'SMM';
            case 'feedpertooth':
                return this.units === 'INCH' ? 'IPT' : 'MMPT';
            case 'feedperrevolution':
                return this.units === 'INCH' ? 'IPR' : 'MMPR';
            case 'removalrate':
                return this.units === 'INCH' ? 'in^3 / min' : 'cm^3 / min';
        }
    }

    toPreferredUnits(type: UnitType, value:number, objectUnits?:string) {
        /* 
            The units from the backend are always in INCH/imperial units.
            Conversion is only needed to metric.
        */
        if (this.units === "METRIC") {
            // convert IN -> METRIC and remove trailing zeroes
            switch(type) {
                case 'length':
                    return parseFloat((value * 25.4).toFixed(2));
                case 'pressure':
                    return Math.round(value * 6.8947572932);
            }
            
        }
        // No change needed
        return value;
    }

    async getSpindles() : Promise<Array<string>> {
        const spindles = await this.fetch('spindles');
        return spindles.map((spindle:any) => spindle.machine_spindle);
    }

    getMaterialStandards() {
        return [
            {"column_name":"", "standard_name": "US"},
            {"column_name":"material_name_en", "standard_name": "European Standard (EN)"},
            {"column_name":"material_name_uk", "standard_name": "British Standard (UK)"},
            {"column_name":"material_name_din", "standard_name": "DIN"},
            {"column_name":"material_name_wnr", "standard_name": "W\u00e9rkstoff (W-nr.)"},
            {"column_name":"material_name_sp", "standard_name": "Spain"},
            {"column_name":"material_name_jp", "standard_name": "Japan"},
            {"column_name":"material_name_fr", "standard_name": "France"}
        ]

    }

    getToolHolders() {
        return [
            {
                name: 'Weldon',
                value: 'weldon'
            },
            {
                name: 'DA Collet',
                value: 'da'
            },
            {
                name: 'ER Collet',
                value: 'er'
            },
            {
                name: 'TG Collet',
                value: 'tg'
            },
            {
                name: 'Shrink-Fit',
                value: 'shrink'
            },
            {
                name: 'Press-Fit',
                value: 'press'
            },
            {
                name: 'Hydraulic',
                value: 'hydraulic'
            },
            {
                name: 'Milling Chuck',
                value: 'chuck'
            }
        ]
    }

    getToolPaths() {
        return [
            {
                name: 'Parallel Offset',
                value: 'offset',
                description: 'A traditional roughing strategy where equidistant offsets follow the shape of the part profile',
                helpModal: ''
            },
            {
                name: 'VoluMill™',
                value: 'hem',
                description: 'An ultra-high performance roughing strategy that maintains a consistent material removal rate and tool load',
                helpModal: ''
            },
            {
                name: 'Trochoidal Slotting',
                value: 'arc',
                description: 'A strategy for milling a slot with a sequence of arcs, enabling improved chip evacuation and higher speed and feed rates',
                helpModal: ''
            },
            {
                name: 'Finishing',
                value: 'finish',
                description: 'A strategy that follows the part profile after most of the material has been removed through roughing operations',
                helpModal: ''
            },
            {
                name: 'Full Slot',
                value: 'slot',
                description: 'A simple strategy where the endmill path follows the centerline of the slot',
                helpModal: ''
            }
        ]
    }

    getDrillCycles() {
        return [
            {
                name: 'Standard Drilling',
                value: 'std',
                description: 'A single drill-in and retract-out cycle, intended for shallow holes where chip evacuation is not problematic',
                helpModal: ''
            },
            {
                name: 'Deep Hole Peck Drilling',
                value: 'peck',
                description: 'Intended for deep drilling, where the tool is periodically retracted to clear the hole of chips',
                helpModal: ''
            },
            {
                name: 'Through-Spindle Coolant Drilling',
                value: 'thru',
                description: 'Coolant is fed directly through the center of the drill, eliminating the need for peck drilling for deep holes',
                helpModal: ''
            }
        ]
    }

    getPrintAttributes(material: MaterialState, machine: MachineState, tool: ToolState) {
        let materialGroups = this.cached.get('material_groups') instanceof Array ? this.cached.get('material_groups') : [];
        let materialGroup = materialGroups.filter((group:any) => group.id === material.materialGroup)[0] || {};

        let mat = {
            'Group': materialGroup.material_description || material.materialGroup,
            'Name': material.name,
            'Hardness': `${material.hardness} ${material.hardnessScale}`,
            'Tensile Strength': `${material.tensileStrength / 1000} ${this.displayPreferredUnits('pressure')}`,
        }
        let mach = {
            'Machine Rigidity': `${machine.rigidity} out of 10`,
            'Spindle Type': machine.spindle,
            'Holder Type': this.getToolHolders().filter(th => th.value === machine.toolHolder)[0].name,
            'Toolpath Type': tool.type === 'endmill' && this.getToolPaths().filter(th => th.value === machine.toolPath)[0].name,
            'Drill Cycle Type': tool.type === 'drill' && this.getDrillCycles().filter(th => th.value === machine.drillCycle)[0].name,
        }
        let too = {
            'Name': tool.name,
            'EDP': tool.edp.toString().padStart(5, '0'),
            'Diameter': `${tool.diameter} ${this.displayPreferredUnits('length')}`,
            'Flutes': tool.flutes,
            'Cut Length': `${tool.cutLength} ${this.displayPreferredUnits('length')}`,
            'Stickout Length': `${tool.toolStickout} ${this.displayPreferredUnits('length')}`,
            'Depth of Cut': tool.type === 'endmill' && `${tool.depthOfCut} ${this.displayPreferredUnits('length')}`,
            'Width of Cut': tool.type === 'endmill' && `${tool.widthOfCut} ${this.displayPreferredUnits('length')}`,
            'Width of Slot': (tool.type === 'endmill' && machine.toolPath === 'arc') && `${tool.widthOfSlot} ${this.displayPreferredUnits('length')}`,
            'Depth of Hole': tool.type === 'drill' && `${tool.depthOfHole} ${this.displayPreferredUnits('length')}`,
        }

        return {
            material: mat,
            machine: mach,
            tool: too
        }
    }

    async getMaterialGroupsAsOptions() {
        const materialGroups = await this.fetch('material_groups', (x:any) => x.hardness_value !== null && x.tensile_strength_psi !== null)


        return materialGroups?.map((group:any, index:number) => {
            let name = group.material_description;
            let description = group.material_info + '\n\nExamples: ' + group.material_examples;

            let helpModal = {
                name,
                description
            }

            return {
                name,
                description,
                value: group.id,
                helpModal,
                ...group
            }
        }) || [];
    }

    async getMaterialsAsObjects(standard = this.standards) {
        const materials = await this.fetch(`materials&standard=${standard}`, null, (material: any) => {
            material.tensile_strength_psi = this.toPreferredUnits('pressure', material.tensile_strength_psi);
            return material;
        });

        this.getSpindles(); // prefetch

        return materials?.map((material:any) => {
            let name = material.material_name;
            let description = material.material_description;
            let value = material.id;

            return {
                image: null,
                name,
                description,
                value,
                attributes: JSON.parse(JSON.stringify(material).replace(/material_/gm, ""))
            }
        }) || [];
    }

    /** Fetches from the backend server.
     * @param url the endpoint to fetch it from "materials" -> "ENDPOINT?fetch=materials"
     * @param filterFn a function that is passed to filter the data (data.filter())
     * @param mapFn a function that is passed to map the data (data.map())
     * @returns the data from the server
     */
    private async fetch(url: string, filterFn?: any, mapFn?: any) : Promise<any> {
        if (this.cached.get(url) === undefined) {
            this.cached.set(url, new Promise(async (resolve) => {
                const resp = await axios.get(ENDPOINT + '?fetch=' + url);
                let filteredData = filterFn ? resp.data.filter(filterFn) : resp.data
                resolve(mapFn ? filteredData.map(mapFn) : filteredData);
            }))
            
            this.cached.set(url, await this.cached.get(url));
        }
        return this.cached.get(url);
    }

    private async postTool(url: 'endmills'|'drills', material: MaterialState, machine : MachineState, filter?: any) : Promise<any> {
        if ((url === 'endmills' && (
                this.cachedEndmillState === null ||
                !isEqual(this.cachedEndmillState[0], material)
                || !isEqual(this.cachedEndmillState[1], machine))
            ) ||
            (url === 'drills' && (
                this.cachedDrillState === null ||
                !isEqual(this.cachedDrillState[0], material) ||
                !isEqual(this.cachedDrillState[1], machine)
            ))
        ) {
            this.cached.set(url, new Promise(async (resolve) => {
                const resp = await axios.post(ENDPOINT + "/" + url, {
                    material,
                    machine,
                    units: this.units
                });
                resolve(filter ? resp.data.filter(filter) : resp.data);
            }))

            if (url === 'endmills') {
                this.cachedEndmillState = [material, machine];
            } else {
                this.cachedDrillState = [material, machine];
            }
            
            this.cached.set(url, await this.cached.get(url));
        }
        return this.cached.get(url);
    }

    fetchIfCached(url: string) {
        return this.cached.get(url) || [];
    }

    // End mills

    async getEndmillsAsObjects(material: MaterialState, machine: MachineState) {
		const endmills = await this.postTool('endmills', material, machine);

        return endmills
            ?.map((endmill:any) => {
                let name = endmill.description;
                let value = endmill.EDP;
                let description = `EDP #${endmill.EDP.toString().padStart(5, '0')}, Series ${endmill.product}`;

                // Convert to preferred units
                let convertedUnits: any = {}
                for (let key of Object.keys(endmill)) {
                    if (['cornerRadius', 'cutLength', 'depthOfCut', 'diameter', 'noseRad', 'toolStickout', 'widthOfCut', 'widthOfSlot'].includes(key)) {
                        convertedUnits[key] = this.toPreferredUnits('length', endmill[key]);
                    }
                }
                endmill = {...endmill, ...convertedUnits};

                return {
                    image: `https://www.garrtool.com/images/product-thumb/${endmill.product}.PNG`,
                    name,
                    description,
                    value,
                    attributes: endmill
                }
            }) || [];
    }

    // Drills 

    async getDrillsAsObjects(material: MaterialState, machine: MachineState) {
        const drills = await this.postTool('drills', material, machine);

        return drills
            ?.map((drill:any) => {
                let name = drill.description;
                let value = drill.EDP;
                let description = `EDP #${drill.EDP.toString().padStart(5, '0')}, Series ${drill.product}`;

                // Convert to preferred units
                let convertedUnits: any = {}
                for (let key of Object.keys(drill)) {
                    if (['cutLength', 'diameter', 'toolStickout', 'depthOfHole'].includes(key)) {
                        convertedUnits[key] = this.toPreferredUnits('length', drill[key]);
                    }
                }
                drill = {...drill, ...convertedUnits};

                return {
                    image: `https://www.garrtool.com/images/product-thumb/${drill.product}.PNG`,
                    name,
                    description,
                    value,
                    attributes: drill
                }
            }) || [];
    }

    // Hardness fetching
    async getHardnessConversions(hardness_value: number, hardness_scale: 'HBS'|'HBW'|'HRB'|'HRC') {
        const resp = await axios.post(ENDPOINT + "/" + 'hardness', {
            hardness_value,
            hardness_scale,
            units: this.units			
        });
        return {
            HBS: resp.data.HBS || null,
            HBW: resp.data.HBW || null,
            HRB: resp.data.HRB || null,
            HRC: resp.data.HRC || null,
            UTS: resp.data.UTS || null
        };
    }
}

export default API;